import { useState, useMemo, useEffect } from 'react'
import { MessageSquare } from 'lucide-react'
import type { ChatOverview, ScopeFilter } from '@/types/teacher-chat'
import { ChatHeader } from './ChatHeader'
import { ChatMessages } from './ChatMessages'
import { ChatInput } from './ChatInput'
import { ChatFacetBar } from './ChatFacetBar'
import {
  ActiveScopeFilter,
  emptyActiveFilter,
  computeScopeFilter,
  scopeFilterKey,
} from './scopeUtils'

interface ChatConversationPanelProps {
  chat: ChatOverview | null
  currentTeacherId: string | undefined
  onClose?: () => void
}

export function ChatConversationPanel({ chat, currentTeacherId, onClose }: ChatConversationPanelProps) {
  const [activeFilter, setActiveFilter] = useState<ActiveScopeFilter>(emptyActiveFilter())

  // Filter beim Chat-Wechsel zuruecksetzen.
  useEffect(() => {
    setActiveFilter(emptyActiveFilter())
  }, [chat?.request_id])

  const scopeFilter: ScopeFilter = useMemo(
    () => computeScopeFilter(chat?.available_facets ?? null, activeFilter),
    [chat?.available_facets, activeFilter]
  )
  const filterKey = scopeFilterKey(scopeFilter)

  if (!chat) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Wähle einen Chat aus</p>
          <p className="text-sm">Klicke links auf einen Chat, um den Verlauf anzuzeigen</p>
        </div>
      </div>
    )
  }

  const showFacets = !!chat.available_facets && (chat.lesson_count ?? 0) > 1

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      <ChatHeader chat={chat} onClose={onClose} />
      {showFacets && (
        <ChatFacetBar
          facets={chat.available_facets!}
          active={activeFilter}
          onChange={setActiveFilter}
        />
      )}
      <ChatMessages
        chat={chat}
        currentTeacherId={currentTeacherId}
        scopeFilter={scopeFilter}
        filterKey={filterKey}
      />
      <ChatInput
        requestId={chat.request_id}
        isReadOnly={chat.is_read_only}
        lessons={chat.lessons ?? []}
        activeScopeFilterIds={
          scopeFilter.scope_filter_lesson_ids && scopeFilter.scope_filter_lesson_ids.length > 0
            ? scopeFilter.scope_filter_lesson_ids
            : null
        }
        filterKey={filterKey}
      />
    </div>
  )
}
